import React, { useMemo, useState } from "react";
import { ChevronDown, Guitar, Search } from "lucide-react";
import { TAB_GROUPS, slugify } from "@/lib/wolfTabs";

const TabBody = ({ s }) => {
  if (!s.tab) {
    return (
      <p className="font-editorial italic text-white/45 text-base" data-testid={`tab-soon-${slugify(s.title)}`}>
        Tab coming soon — basic chords and riff for this one are on the way.
      </p>
    );
  }
  return (
    <div data-testid={`tab-content-${slugify(s.title)}`}>
      <div className="flex flex-wrap gap-5 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80 mb-4">
        <span>Tuning · {s.tuning || "Standard"}</span>
        <span>Capo · {s.capo ?? "None"}</span>
        {s.key && <span>Key · {s.key}</span>}
      </div>
      <pre className="font-mono text-[13px] sm:text-sm leading-relaxed text-white/85 whitespace-pre overflow-x-auto bg-black/60 border border-white/10 p-4 sm:p-5">{s.tab}</pre>
    </div>
  );
};

const SongRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  const id = slugify(s.title);
  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`tab-toggle-${id}`}
        className="w-full flex items-center justify-between gap-4 py-4 sm:py-5 text-left group"
      >
        <span className="flex items-center gap-3 min-w-0">
          <Guitar size={15} className={s.tab ? "text-[#D4AF37]" : "text-white/25"} />
          <span className="font-display tracking-wider text-lg sm:text-xl text-white group-hover:text-[#D4AF37] transition-colors truncate">{s.title}</span>
        </span>
        <span className="flex items-center gap-3 shrink-0">
          <span className={`text-[9px] uppercase tracking-[0.25em] px-2 py-1 border ${s.tab ? "text-[#D4AF37] border-[#D4AF37]/50" : "text-white/35 border-white/15"}`}>
            {s.tab ? "Tab" : "Soon"}
          </span>
          <ChevronDown size={16} className={`text-white/50 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && <div className="pb-6" data-testid={`tab-panel-${id}`}><TabBody s={s} /></div>}
    </div>
  );
};

export const SongTabsSection = () => {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return TAB_GROUPS;
    return TAB_GROUPS.map((g) => ({ ...g, songs: g.songs.filter((s) => s.title.toLowerCase().includes(needle)) })).filter((g) => g.songs.length);
  }, [q]);
  const total = TAB_GROUPS.reduce((n, g) => n + g.songs.length, 0);
  const ready = TAB_GROUPS.reduce((n, g) => n + g.songs.filter((s) => s.tab).length, 0);

  return (
    <section className="mt-24 sm:mt-32" data-testid="dawg-tabs-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">— Guitar Tabs · {total} Songs</p>
          <h2 className="font-display wolf-gold-foil text-4xl sm:text-6xl leading-none">Learn The Riffs</h2>
          <p className="font-editorial italic text-lg sm:text-xl text-white/55 mt-5 max-w-xl">
            Basic guitar tabs for every released WOLF song. Pick a track, open the tab, play along with the recording in WOLF DAWG.
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mt-4" data-testid="dawg-tabs-count">{ready} of {total} tabs live · more dropping soon</p>
        </div>
        <label className="relative w-full lg:w-80">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a song…"
            data-testid="dawg-tabs-search"
            className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none text-white placeholder-white/30 pl-11 pr-4 py-3.5 text-sm transition-colors"
          />
        </label>
      </div>

      <div className="mt-12 space-y-10">
        {groups.length === 0 && <p className="text-white/45 font-editorial italic" data-testid="dawg-tabs-empty">No song matches "{q}".</p>}
        {groups.map((g) => (
          <div key={g.release} className="border border-white/10 bg-[#0d0d0d]" data-testid={`tab-group-${slugify(g.release)}`}>
            <div className="flex items-center gap-4 px-5 sm:px-8 py-5 border-b border-[#D4AF37]/25">
              <img src={g.cover} alt={g.release} className="w-12 h-12 object-cover border border-white/10" loading="lazy" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Release</p>
                <p className="font-display text-xl sm:text-2xl tracking-wider text-white">{g.release}</p>
              </div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-white/35">{g.songs.length} {g.songs.length === 1 ? "song" : "songs"}</span>
            </div>
            <div className="px-5 sm:px-8">
              {g.songs.map((s) => <SongRow key={s.title} s={s} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
